import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Video, Trash2, Upload, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import RecordingUploadModal from '@/components/modals/RecordingUploadModal';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function CreateLiveClass({ user, onLogout }) {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [recordingModal, setRecordingModal] = useState({ isOpen: false, liveClass: null });
  const [endedClassesNeedingRecordings, setEndedClassesNeedingRecordings] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    meeting_url: '',
    scheduled_at: '',
    duration_minutes: 60
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchClasses();
    checkEndedClassesNeedingRecordings();
  }, []);

  const checkEndedClassesNeedingRecordings = async () => {
    try {
      const res = await axios.get(`${API}/live-classes/ended-without-recordings`, { headers: getAuthHeaders() });
      setEndedClassesNeedingRecordings(res.data);
      
      // Auto-show popup for the first ended class without recording
      if (res.data.length > 0) {
        const firstEndedClass = res.data[0];
        // Only show if class ended within last 24 hours to avoid spam
        const classEndTime = new Date(firstEndedClass.scheduled_at);
        classEndTime.setMinutes(classEndTime.getMinutes() + firstEndedClass.duration_minutes);
        const now = new Date();
        const hoursAfterEnd = (now - classEndTime) / (1000 * 60 * 60);
        
        if (hoursAfterEnd <= 24) {
          setTimeout(() => {
            setRecordingModal({ isOpen: true, liveClass: firstEndedClass });
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Failed to check ended classes:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      const res = await axios.get(`${API}/live-classes`, { headers: getAuthHeaders() });
      setClasses(res.data);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.meeting_url.trim() || !formData.scheduled_at) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/live-classes`, formData, { headers: getAuthHeaders() });
      toast.success('Live class scheduled successfully!');
      setFormData({ title: '', description: '', meeting_url: '', scheduled_at: '', duration_minutes: 60 });
      setShowForm(false);
      fetchClasses();
    } catch (error) {
      console.error('Failed to create class:', error);
      toast.error('Failed to schedule class');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this live class?')) return;

    try {
      await axios.delete(`${API}/live-classes/${id}`, { headers: getAuthHeaders() });
      toast.success('Live class deleted');
      fetchClasses();
    } catch (error) {
      console.error('Failed to delete class:', error);
      toast.error('Failed to delete class');
    }
  };

  const openRecordingModal = (liveClass) => {
    setRecordingModal({ isOpen: true, liveClass });
  };

  const handleUploadComplete = (recording) => {
    toast.success('Recording uploaded successfully!');
    setRecordingModal({ isOpen: false, liveClass: null });
    fetchClasses();
    checkEndedClassesNeedingRecordings();
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (liveClass) => {
    const now = new Date();
    const scheduledTime = new Date(liveClass.scheduled_at);
    const endTime = new Date(scheduledTime.getTime() + liveClass.duration_minutes * 60000);

    if (liveClass.status === 'cancelled') {
      return <Badge variant="destructive">Cancelled</Badge>;
    } else if (now < scheduledTime) {
      return <Badge variant="secondary" className="bg-blue-100 text-blue-800">Scheduled</Badge>;
    } else if (now >= scheduledTime && now <= endTime) {
      return <Badge variant="secondary" className="bg-green-100 text-green-800">Live</Badge>;
    } else {
      return <Badge variant="secondary" className="bg-gray-100 text-gray-800">Completed</Badge>;
    }
  };

  if (loading) {
    return <Layout user={user} onLogout={onLogout} currentPage="create-live-class"><div className="p-8">Loading...</div></Layout>;
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="create-live-class">
      <div className="p-6 lg:p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ color: '#d86a2e' }}>🎥 Live Classes</h1>
            <p className="text-lg text-gray-600">View, schedule, and manage live sessions</p>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="font-semibold"
            style={{ background: '#ff9d5c' }}
          >
            {showForm ? 'Cancel' : '+ Schedule Class'}
          </Button>
        </div>

        {showForm && (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#d86a2e' }}>Schedule New Live Class</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Title *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Class title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Description</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Class description..."
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Meeting URL *</label>
                  <Input
                    value={formData.meeting_url}
                    onChange={(e) => setFormData({ ...formData, meeting_url: e.target.value })}
                    placeholder="https://meet.google.com/..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Scheduled Date & Time *</label>
                    <Input
                      type="datetime-local"
                      value={formData.scheduled_at}
                      onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Duration (minutes)</label>
                    <Input
                      type="number"
                      value={formData.duration_minutes}
                      onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                      min="15"
                      step="15"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full font-semibold"
                  style={{ background: '#ff9d5c' }}
                >
                  {submitting ? 'Scheduling...' : 'Schedule Live Class'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        <div className="space-y-3">
          {classes.length === 0 ? (
            <Card className="border-0 shadow-lg">
              <CardContent className="p-12 text-center">
                <Video className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-xl font-semibold text-gray-600">No live classes scheduled</p>
              </CardContent>
            </Card>
          ) : (
            classes.map((liveClass) => {
              const now = new Date();
              const scheduledTime = new Date(liveClass.scheduled_at);
              const endTime = new Date(scheduledTime.getTime() + liveClass.duration_minutes * 60000);
              const isCompleted = now > endTime && liveClass.status !== 'cancelled';
              const needsRecording = isCompleted && !liveClass.recording_uploaded;

              return (
                <Card key={liveClass.id} className={`border-0 shadow-lg ${needsRecording ? 'ring-2 ring-orange-200 bg-orange-50' : ''}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-bold">{liveClass.title}</h3>
                          {getStatusBadge(liveClass)}
                          {liveClass.recording_uploaded && (
                            <Badge variant="secondary" className="bg-green-100 text-green-800 flex items-center space-x-1">
                              <CheckCircle className="w-3 h-3" />
                              <span>Recording Available</span>
                            </Badge>
                          )}
                        </div>
                        <p className="text-gray-600 mb-2">{liveClass.description}</p>
                        <div className="space-y-1 text-sm text-gray-500">
                          <p>{formatDateTime(liveClass.scheduled_at)} • {liveClass.duration_minutes} min</p>
                          <p className="text-blue-600">{liveClass.meeting_url}</p>
                        </div>
                        
                        {needsRecording && (
                          <div className="mt-3 p-3 bg-orange-100 rounded-lg border border-orange-200">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-semibold text-orange-800">Recording Upload Required</p>
                                <p className="text-xs text-orange-600">Students are waiting for the class recording</p>
                              </div>
                              <Button
                                onClick={() => openRecordingModal(liveClass)}
                                size="sm"
                                className="bg-orange-600 hover:bg-orange-700 text-white text-xs px-3 py-1"
                              >
                                <Upload className="w-3 h-3 mr-1" />
                                Upload Recording
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex space-x-2">
                        {isCompleted && !liveClass.recording_uploaded && (
                          <Button
                            onClick={() => openRecordingModal(liveClass)}
                            size="sm"
                            variant="outline"
                            className="border-primary-blue text-primary-blue hover:bg-primary-blue hover:text-white"
                          >
                            <Upload className="w-3 h-3 mr-1" />
                            Add Recording
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(liveClass.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Recording Upload Modal */}
        <RecordingUploadModal
          isOpen={recordingModal.isOpen}
          onClose={() => setRecordingModal({ isOpen: false, liveClass: null })}
          liveClass={recordingModal.liveClass}
          onUploadComplete={handleUploadComplete}
        />
      </div>
    </Layout>
  );
}